import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, GraduationCap } from "lucide-react";
import { useNoIndex } from "@/lib/useSeo";

export default function NewsletterUnsubscribe() {
  useNoIndex();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [data, setData] = useState({ email: "", status: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Lien invalide. Aucun token fourni.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/newsletter/unsubscribe?token=${encodeURIComponent(token)}`);
        setData(data);
        setStatus("ok");
      } catch (e) {
        setStatus("error");
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="newsletter-unsubscribe-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center">
            <GraduationCap size={22} />
          </div>
          <span className="font-display text-xl font-bold text-slate-900">Student Hub</span>
        </div>

        {status === "loading" && (
          <div className="space-y-4">
            <Loader2 size={36} className="animate-spin text-violet-500 mx-auto" />
            <p className="text-slate-600">Désinscription en cours…</p>
          </div>
        )}

        {status === "ok" && (
          <div className="space-y-4" data-testid="unsub-success">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900">
              {data.status === "already_unsubscribed" ? "Déjà désinscrit" : "Désinscription confirmée"}
            </h1>
            <p className="text-slate-600">
              {data.email && <span className="block text-sm text-slate-500 mb-2">{data.email}</span>}
              Tu ne recevras plus la newsletter Student Hub. Tu peux te réinscrire à tout moment depuis nos articles de blog.
            </p>
            <Link
              to="/blog"
              className="inline-block px-5 py-2.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800"
            >
              Retour au blog
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4" data-testid="unsub-error">
            <XCircle size={48} className="text-red-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Lien invalide</h1>
            <p className="text-slate-600">{error}</p>
            <Link
              to="/"
              className="inline-block px-5 py-2.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800"
            >
              Retour à l'accueil
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
